/**
 * @module ui-live-suggest
 */
import ee from "lib/eventemitter";
import { removeDirtyChars, qs, qsa, getProp, interceptEventCallback } from "utils/utils";
import throttle from "lodash/throttle";
import { API_PB_RENDER_FEATURE } from "lib/gamservices";
import { validStatus, handleJSON, handleRenderFeature } from "utils/fetch";
import { sendAnalyticsEvent } from "../lib/search/analytics";
import debounce from "lodash/debounce";

// (ʘ‿ʘ) YOLO, here is an OLOO pattern.
const LiveSuggestFeature = {
  init(el, results) {
    this.el = el;
    this.results = results;
    this.attrs = el.dataset.livesuggest ? JSON.parse(el.dataset.livesuggest) : {};
    this.analytics = null;
    this.debouncedSendAnalytics = debounce(this.sendAnalytics, 2000);

    this.results.addEventListener("click", (e) => {
      const button = e.target.closest(".js-c-button__search-close");
      const showAllResultsLink = e.target.closest(".js-onclick--search");

      if (button && button.dataset.close) {
        this.results.innerHTML = "";
      }

      if (showAllResultsLink) { // Trigger the full search form submission upon "Show All Results" link click.
        e.preventDefault();
        const searchButton = qs(".c-search-box__controls .c-button", this.results.parentNode);

        if (searchButton){
          searchButton.click();
        }
      }

    }, false);
  },

  get isFinancialSearch() {
    return this.el.closest(".o-financial-search");
  },

  config() {
    const isSecuritySearchSection = getProp(window, "tgam.meta.isSecuritySearchSection");
    const type = isSecuritySearchSection && this.isFinancialSearch ? "financial symbol search" : "modal";
    const config = {
      mode: this.attrs.mode || "all",
      themeType: this.attrs.themeType || "dark",
      closeButton: this.attrs.closeButton || false,
      query: removeDirtyChars(this.el.value.trim()),
      type
    };
    const customFields = encodeURIComponent(JSON.stringify(config));
    return { customFields };
  },

  render(data = "") {
    this.results.innerHTML = data || "";
    if (data !== "") {
      this.debouncedSendAnalytics(data);
    }
  },

  reset() {
    this.el.value = "";
    this.render("");
  },

  sendAnalytics(data) {
    let searchData;
    let resultsShown;
    let securitiesShown;
    //means livesuggest has some results
    const searchResultsExist = /data-search/.test(data);
    if (searchResultsExist) {
      const liveSuggest = qs(".arc-f-live-suggest-output");
      if (!liveSuggest) {
        return;
      }
      searchData = JSON.parse(liveSuggest.dataset.search);
      const { total, securitiesTotal } = searchData;
      resultsShown = Math.min(total, 3);
      securitiesShown = Math.min(securitiesTotal, 2);
    }
    const isSecuritySearchSection = getProp(window, "tgam.meta.isSecuritySearchSection");
    const mode = isSecuritySearchSection && this.isFinancialSearch ? "securities" : "all";
    const type = isSecuritySearchSection && this.isFinancialSearch ? "financial symbol search" : "modal";
    const clientAnalytics = {
      query: this.el.value,
      isLs: true,
      resultsShown,
      securitiesShown,
      mode,
      type
    };
    this.analytics = { ...clientAnalytics, ...searchData };
    sendAnalyticsEvent(this.analytics);
  },
};

/**
 * @method fetchSuggestions
 * @description Fetch the result(s) for the user input
 * @param {String} customFields - with encoded custom fields
 * @returns {Promise<String>}
 */
function fetchSuggestions(customFields) {
  const pbRenderAPI = `${API_PB_RENDER_FEATURE}/search/live-suggest?customFields=${customFields}`;
  return fetch(pbRenderAPI)
    .then(validStatus)
    .then(handleJSON)
    .then(handleRenderFeature);
}

/**
 * @method liveSuggestInput
 * @description Initialize live suggestion results for form input.
 * @param {Element} input - input field to initialize
 * @param {Element} output - output div for results
 */
function liveSuggestInput(input, output) {
  const lsf = Object.create(LiveSuggestFeature);
  lsf.init(input, output);

  lsf.el.addEventListener("keyup", throttle(event => {
    // minimum required input length (server-side checks length too)
    if (event.target.value.length >= 2) {
      fetchSuggestions(lsf.config().customFields)
        .then(results => lsf.render(results));
    } else {
      lsf.render(""); // otherwise clear results
    }
  }, 250), false);

  ee.on("menu-panel:closed", () => lsf.reset());
}

/**
 * Initialize the financial and header search upon user focus
 */
export function liveSuggestInit() {
  const suggestResults = qsa(".js-live-suggest-output");
  qsa(".js-live-suggest-input").forEach((input, i) => {
    interceptEventCallback(input, "focus", () => {
      liveSuggestInput(input, suggestResults[i]);
    });
  });
}
